<script lang="ts">import { cfgToThemeCdnPath, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { loggedIn, toSiteUrl } from "@infinitro/global";
import { RemoteIcon, SearchIcon } from "@infinitro/icons";
import { tick } from "svelte";
import { cubicOut } from "svelte/easing";
import { writable } from "svelte/store";
import DropdownArrow from "./icons/dropdown_arrow.svelte";
let { value = $bindable(), id, placeholder: placeholderProp, disabled = false, disabledWithoutStyles = false, icon = null, customClasses = "", inputClasses = "", iconClasses = "", size = "large", options = [], width = "100%", disableMinWidth = false, withIcon = false, customStyles = "", hideValue = false, affectsFlow = false, menuPosition = "bottom", stateToggle = writable(null), isOpen = $bindable(false), enableSearch = false, searchPlaceholder: searchPlaceholderProp, smoothPanel = true, portalMenu = false, menuClasses = "", onChange, components, componentsEnd } = $props();
$effect.pre(() => {
	if (value === undefined) value = "";
});
const placeholder = $derived(placeholderProp ?? $t(Verbiage.select_an_option));
const searchPlaceholder = $derived(searchPlaceholderProp ?? $t(Verbiage.search));
let selectedOptionLabel = $state("");
let selectedOptionIcon = $state(null);
let selectedOptionIconProps = $state({});
let containerElement = $state(undefined);
let menuElement = $state(undefined);
let menuFixedStyle = $state("");
let searchQuery = $state("");
let activeOptionIndex = $state(-1);
let autoFlipUp = $state(false);
/** Move the menu node to <body> so no overflow/scroll ancestor (e.g. a modal) can clip it. */
function portal(node) {
	document.body.appendChild(node);
	return { destroy() {
		node.parentNode?.removeChild(node);
	} };
}
function dropdownPanelTransition(_node, { y, enabled }) {
	if (!enabled) {
		return { duration: 0 };
	}
	return {
		duration: 280,
		easing: cubicOut,
		css: (t, u) => `opacity: ${t}; transform: translateY(${y * u}px);`
	};
}
function startAtSelectedIndex() {
	const selectedIndex = filteredOptions.findIndex((option) => option.value === value);
	activeOptionIndex = selectedIndex >= 0 ? selectedIndex : 0;
}
/**
* Flip the menu upward when there isn't room below (e.g. a short viewport) and there's more
* room above — otherwise keep the default. An explicit menuPosition="top" always wins.
* When portalMenu is set, also compute the fixed-position style anchored to the input's rect —
* this keeps the menu out of any ancestor's overflow/scroll box entirely (e.g. a modal), so
* unlike toggling an ancestor's overflow, it never changes that ancestor's layout or scroll.
*/
function computeAutoFlip() {
	if (!containerElement) {
		autoFlipUp = menuPosition === "top";
		return;
	}
	const rect = containerElement.getBoundingClientRect();
	const estimatedMenuHeight = 216;
	const spaceBelow = window.innerHeight - rect.bottom;
	const spaceAbove = rect.top;
	autoFlipUp = menuPosition === "top" || spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
	if (portalMenu) {
		const parts = [`left: ${rect.left}px`, `--dropdown-anchor-width: ${rect.width}px`];
		if (autoFlipUp) {
			parts.push(`bottom: ${Math.max(window.innerHeight - rect.top + 4, 0)}px`);
		} else {
			parts.push(`top: ${rect.bottom + 4}px`);
		}
		menuFixedStyle = parts.join("; ");
	}
}
function openMenu() {
	isOpen = true;
	startAtSelectedIndex();
	computeAutoFlip();
}
function toggleDropdown(event) {
	event.stopPropagation();
	if (disabled || disabledWithoutStyles) {
		return;
	}
	if (isOpen) {
		isOpen = false;
		searchQuery = "";
		activeOptionIndex = -1;
	} else {
		openMenu();
	}
}
function moveActiveOption(delta) {
	if (!filteredOptions.length) {
		return;
	}
	activeOptionIndex = activeOptionIndex < 0 ? delta > 0 ? 0 : filteredOptions.length - 1 : (activeOptionIndex + delta + filteredOptions.length) % filteredOptions.length;
}
function handleComboboxKeydown(event) {
	if (disabled || disabledWithoutStyles) {
		return;
	}
	switch (event.key) {
		case "ArrowDown":
		case "ArrowUp":
			event.preventDefault();
			if (!isOpen) {
				openMenu();
			}
			moveActiveOption(event.key === "ArrowDown" ? 1 : -1);
			break;
		case "Enter":
			event.preventDefault();
			event.stopPropagation();
			if (isOpen) {
				selectActiveOrFirstOption();
			} else {
				openMenu();
			}
			break;
		case " ":
			if (!isOpen) {
				event.preventDefault();
				openMenu();
			}
			break;
		case "Escape":
			if (isOpen) {
				event.preventDefault();
				isOpen = false;
				searchQuery = "";
				activeOptionIndex = -1;
			}
			break;
	}
}
function selectActiveOrFirstOption() {
	if (!filteredOptions.length) {
		return;
	}
	const index = activeOptionIndex >= 0 ? activeOptionIndex : 0;
	const option = filteredOptions[index];
	if (option) {
		handleOptionSelect(undefined, option);
	}
}
function handleSearchKeydown(event) {
	if (disabled || disabledWithoutStyles) {
		return;
	}
	switch (event.key) {
		case "ArrowDown":
			event.preventDefault();
			event.stopPropagation();
			moveActiveOption(1);
			break;
		case "ArrowUp":
			event.preventDefault();
			event.stopPropagation();
			moveActiveOption(-1);
			break;
		case "Enter":
			event.preventDefault();
			event.stopPropagation();
			selectActiveOrFirstOption();
			break;
		case "Escape":
			event.preventDefault();
			event.stopPropagation();
			isOpen = false;
			searchQuery = "";
			activeOptionIndex = -1;
			break;
	}
}
function handleOptionSelect(event, option) {
	event?.stopPropagation();
	value = option.value;
	selectedOptionLabel = option.label;
	selectedOptionIcon = option.icon;
	selectedOptionIconProps = option.iconProps || {};
	isOpen = false;
	searchQuery = "";
	activeOptionIndex = -1;
	onChange?.(option.value);
	tick();
}
function handleClickOutside(event) {
	const target = event.target;
	// menuElement may live in <body> (portalMenu) — treat clicks inside it as inside the dropdown.
	if (!containerElement?.contains(target) && !menuElement?.contains(target)) {
		isOpen = false;
		searchQuery = "";
		activeOptionIndex = -1;
		tick();
	}
}
const normalizedSearchQuery = $derived(searchQuery.trim().toLowerCase());
const filteredOptions = $derived(enableSearch && normalizedSearchQuery.length > 0 ? options.filter((option) => {
	const label = option.label?.toLowerCase() ?? "";
	const optionValue = option.value?.toLowerCase() ?? "";
	return label.includes(normalizedSearchQuery) || optionValue.includes(normalizedSearchQuery);
}) : options);
const useAlternative = $derived($stateUi.dropdown?.dropdownStyle === "alternative");
const useAlternative2 = $derived($stateUi.dropdown?.dropdownStyle === "alternative2");
const useAlternative4 = $derived($stateUi.dropdown?.dropdownStyle === "alternative4");
const useDropdownBackgroundImage = $derived($stateUi.dropdown.useDropdownBackgroundImage);
const applyBackgroundBlur = $derived($stateUi.dropdown?.applyBackgroundBlur ?? false);
const useGlassDropdownPanel = $derived($stateUi.dropdown?.useGlassDropdownPanel ?? false);
const useFlatDropdownBlur = $derived(applyBackgroundBlur && !useGlassDropdownPanel);
const panelFlyY = $derived(autoFlipUp ? 6 : -6);
const useRemoteIcons = $derived($stateUi.dropdown?.useRemoteIcons ?? false);
$effect(() => {
	const unsub = stateToggle.subscribe((info) => {
		if (info === null) {
			return;
		}
		toggleDropdown(info);
	});
	return unsub;
});
$effect(() => {
	const handler = (event) => handleClickOutside(event);
	document.addEventListener("click", handler, true);
	return () => document.removeEventListener("click", handler, true);
});
$effect(() => {
	const selectedOption = options.find((option) => option.value === value);
	if (selectedOption) {
		selectedOptionLabel = selectedOption.label;
		selectedOptionIcon = selectedOption.icon;
		selectedOptionIconProps = selectedOption.iconProps || {};
	} else {
		selectedOptionLabel = placeholder;
		selectedOptionIcon = icon;
		selectedOptionIconProps = {};
	}
});
$effect(() => {
	if (isOpen && activeOptionIndex >= filteredOptions.length) {
		activeOptionIndex = Math.max(filteredOptions.length - 1, 0);
	}
});
// Re-flip while open if the viewport is resized (e.g. the window is shrunk vertically), and —
// for a portaled menu — also track scrolling of any ancestor so the fixed menu stays anchored.
$effect(() => {
	if (!isOpen) return;
	const handler = () => computeAutoFlip();
	window.addEventListener("resize", handler);
	if (portalMenu) {
		window.addEventListener("scroll", handler, true);
	}
	return () => {
		window.removeEventListener("resize", handler);
		if (portalMenu) {
			window.removeEventListener("scroll", handler, true);
		}
	};
});
</script>

{#snippet menuInner()}
	{#if enableSearch}
		<div
			class="search-container"
			class:search-container-blur={applyBackgroundBlur}
			onclick={(e) => e.stopPropagation()}
		>
			<div class="search-icon">
				<SearchIcon />
			</div>
			<input
				type="text"
				placeholder={searchPlaceholder}
				bind:value={searchQuery}
				class="search-input"
				onclick={(e) => e.stopPropagation()}
				onkeydown={handleSearchKeydown}
			/>
		</div>
	{/if}
	{#each filteredOptions as option, index (option.value)}
		<!-- svelte-ignore a11y_interactive_supports_focus -->
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<div
			class:option-item-alternative={useAlternative || useAlternative4}
			class:option-item-alternative2={useAlternative2}
			class="option-item"
			role="option"
			aria-selected={option.value === value}
			onclick={(e) => handleOptionSelect(e, option)}
			class:selected={option.value === value}
			class:active={index === activeOptionIndex}
			class:first={index === 0}
			class:last={index === filteredOptions.length - 1}
		>
			{#if withIcon && typeof option.icon === 'string'}
				<img src={option.icon} alt={$t(Verbiage.icon_alt, 'alts')} class="option-icon" />
			{:else if withIcon && option.icon !== null}
				{@const OptionIcon = option.icon}
				<OptionIcon {...option.iconProps ?? {}} class="option-icon" />
			{/if}
			<span class={option.icon !== null ? 'option-label' : ''}>{option.label}</span>
		</div>
	{/each}
{/snippet}

<div class="container" bind:this={containerElement} style="--width: {width};">
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div
		class:input-container-alternative={useAlternative || useAlternative4}
		class:input-container-alternative2={useAlternative2}
		class:input-container-alternative4={useAlternative4}
		class:alt2-open={useAlternative2 && isOpen}
		class={`input-container ${size} ${customClasses} ${disabled ? 'input-disabled' : ''} ${disabledWithoutStyles ? 'disabled-without-styles' : ''}  ${isOpen ? 'input-border-hover-active' : ''} ${disableMinWidth ? '' : 'with-min-width'}`}
		onclick={toggleDropdown}
		style="width: {width}; {customStyles}"
	>
		{#if withIcon && typeof selectedOptionIcon === 'string'}
			<div class={`input-icon ${size} ${iconClasses}`}>
				<img src={selectedOptionIcon} alt={$t(Verbiage.icon_alt, 'alts')} />
			</div>
		{:else if withIcon && selectedOptionIcon !== null}
			{@const Icon = selectedOptionIcon}
			<div class={`input-icon ${size} ${iconClasses}`}>
				<Icon {...selectedOptionIconProps} />
			</div>
		{/if}
		{#if useDropdownBackgroundImage}
			<div class="dropdown-background-image-container">
				<img
					src={cfgToThemeCdnPath('/button/background.png')}
					class="dropdown-background-image"
					alt={$t(Verbiage.illustration_alt, 'alts')}
				/>
			</div>
		{/if}
		{@render components?.()}
		<!-- svelte-ignore a11y_role_has_required_aria_props -->
		<div
			{id}
			class={`input ${inputClasses}`}
			class:input-alternative={useAlternative || useAlternative4}
			role="combobox"
			tabindex={disabled || disabledWithoutStyles ? -1 : 0}
			aria-expanded={isOpen}
			aria-disabled={disabled || disabledWithoutStyles}
			onkeydown={handleComboboxKeydown}
		>
			<span class={value ? 'text-white' : 'text-gray-400'}
				>{!hideValue ? selectedOptionLabel : ''}</span
			>
			<div class="input-end">
				{@render componentsEnd?.()}
				{#if !disabledWithoutStyles}
					{#if useRemoteIcons}
						<RemoteIcon icon="ArrowIcon" />
					{:else}
						<DropdownArrow fill={isOpen ? 'white' : '#9CA3AF'} />
					{/if}
				{/if}
			</div>
		</div>
		{#if isOpen && !disabled && !disabledWithoutStyles && !affectsFlow && !portalMenu}
			<div
				class:options-list-alternative={useAlternative || useAlternative4}
				class:options-list-alternative2={useAlternative2}
				class:options-list-alternative2-glass={useAlternative2 && useGlassDropdownPanel}
				class:options-list-alternative4={useAlternative4}
				class:options-list-blur={useFlatDropdownBlur}
				class:menu-position-up={autoFlipUp}
				class="options-list absolute-position {size} {menuClasses}"
				transition:dropdownPanelTransition={{ y: panelFlyY, enabled: smoothPanel }}
			>
				{@render menuInner()}
			</div>
		{/if}
	</div>
	{#if isOpen && !disabled && !disabledWithoutStyles && !affectsFlow && portalMenu}
		<div
			use:portal
			bind:this={menuElement}
			class:options-list-alternative={useAlternative || useAlternative4}
			class:options-list-alternative2={useAlternative2}
			class:options-list-alternative2-glass={useAlternative2 && useGlassDropdownPanel}
			class:options-list-alternative4={useAlternative4}
			class:options-list-blur={useFlatDropdownBlur}
			class="options-list fixed-position {menuClasses}"
			style={menuFixedStyle}
			transition:dropdownPanelTransition={{ y: panelFlyY, enabled: smoothPanel }}
		>
			{@render menuInner()}
		</div>
	{/if}
	{#if isOpen && !disabled && !disabledWithoutStyles && affectsFlow}
		<div
			class:options-list-alternative={useAlternative || useAlternative4}
			class:options-list-alternative2={useAlternative2}
			class:options-list-alternative2-glass={useAlternative2 && useGlassDropdownPanel}
			class:options-list-alternative4={useAlternative4}
			class:options-list-blur={useFlatDropdownBlur}
			class="options-list flow-content {size}"
			transition:dropdownPanelTransition={{ y: panelFlyY, enabled: smoothPanel }}
		>
			{#if enableSearch}
				<div
					class="search-container"
					class:search-container-blur={applyBackgroundBlur}
					onclick={(e) => e.stopPropagation()}
				>
					<div class="search-icon">
						<SearchIcon />
					</div>
					<input
						type="text"
						placeholder={searchPlaceholder}
						bind:value={searchQuery}
						class="search-input"
						onclick={(e) => e.stopPropagation()}
						onkeydown={handleSearchKeydown}
					/>
				</div>
			{/if}
			{#each filteredOptions as option, index (option.value)}
				<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
				<a href={toSiteUrl(option.value, $loggedIn)}>
					<div
						class="option-item"
						role="option"
						aria-selected={option.value === value}
						class:selected={option.value === value}
						class:active={index === activeOptionIndex}
						class:option-item-alternative2={useAlternative2}
						class:first={index === 0}
						class:last={index === filteredOptions.length - 1}
					>
						{#if withIcon && typeof option.icon === 'string'}
							<img src={option.icon} alt={$t(Verbiage.icon_alt, 'alts')} class="option-icon" />
						{:else if withIcon && option.icon !== null}
							{@const OptionIcon = option.icon}
							<OptionIcon {...option.iconProps ?? {}} class="option-icon" />
						{/if}
						<span class={option.icon !== null ? 'option-label' : ''}>{option.label}</span>
					</div>
				</a>
			{/each}
		</div>
	{/if}
</div>

<style>
	.container {
		display: flex;
		width: 100%;
		flex-direction: column;
		font-family: var(--font-main);
		width: var(--width);
}
	.input-container {
		position: relative;
		display: flex;
		cursor: pointer;
		align-items: center;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-300);
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
}
	.input-container-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-500);
		border-radius: 6px;
		background-image: unset;
}
	.input-container-alternative:not(.disabled-without-styles):hover {
		background-image: unset !important;
	}

	.input-container-alternative2 {
		position: relative;
		border-radius: 8px;
		border-style: none;
		background-image: unset;
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-background-500) 100%
		) !important;
}

	.input-container-alternative2::before {
		position: absolute;
		inset: 0px;
		border-radius: 8px;
		padding: 1px;
		content: '';
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
}

	.input-container-alternative4 {
		border-width: 0px;
}

	.alt2-open::before {
		background: linear-gradient(
			180deg,
			rgba(var(--color-primary-500-rgb) / 0.9) 0%,
			transparent 100%
		);
	}

	.input-container:not(.disabled-without-styles) {
		cursor: pointer;
}

	.input-container:not(.disabled-without-styles):hover {
		border-color: var(--color-secondary-500);
}

	.input-container:not(.disabled-without-styles):active {
		border-color: var(--color-secondary-500);
}

	.input-container:not(.disabled-without-styles):focus-within {
		border-color: var(--color-primary-500);
		outline-color: var(--color-primary-500);
	}
	.input-container:not(.disabled-without-styles):focus-within,
	.input-container:not(.disabled-without-styles):hover,
	.input-container:not(.disabled-without-styles).input-border-hover-active {
		border-color: var(--color-primary-500);
		outline-color: var(--color-primary-500);
	}
	.input-container:not(.disabled-without-styles).input-disabled {
		cursor: not-allowed;
		opacity: 0.25;
		background: var(--gradient-alternative-4);
}
	.with-min-width {
		min-width: 120px;
}
	.input-icon {
		margin-right: -12px;
		margin-left: 8px;
		display: flex;
		height: 100%;
		width: 30px;
		align-items: center;
		justify-content: center;
}
	.input-icon.extra-large {
		width: 45px;
}
	.input-icon img {
		height: 20px;
}
	.input-icon.small img {
		height: 16px;
}
	.input {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		text-wrap: nowrap;
		border-radius: 16px;
		background-color: transparent;
		padding-right: 8px;
		padding-left: 15px;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
}

	.input-alternative {
		border-radius: 12px;
}

	.input:focus {
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	.input-end {
		display: flex;
		align-items: center;
		gap: 0.5rem;
}
	.search-container {
		position: relative;
		margin-bottom: 0.5rem;
		margin-top: 0.5rem;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 0.5rem;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
}

	.search-container-blur {
		background: var(--color-highlight-3);
		backdrop-filter: blur(8px);
		will-change: backdrop-filter;
		background-image: unset;
	}
	.search-input {
		width: 100%;
		border-radius: 8px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--component-input-background);
		padding: 0.25rem;
		padding-left: 2.5rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.search-input:focus {
		outline: 2px solid transparent;
		outline-offset: 2px;
		--tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
		--tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
		box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
		--tw-ring-color: var(--color-secondary-500);
}

	.search-icon {
		position: absolute;
		left: 1.25rem;
		display: flex;
		align-items: center;
}

	.options-list-blur .search-container :global(.input-container) {
		background: var(--color-highlight-3);
		backdrop-filter: blur(64px);
		will-change: backdrop-filter;
		background-image: unset;
	}
	.options-list-blur .search-container :global(.input-container:hover),
	.options-list-blur .search-container :global(.input-container:focus-within) {
		border-color: transparent;
	}
	.options-list {
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-500);
		--tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		&::-webkit-scrollbar {
				width: 0.375rem;
		}
		&::-webkit-scrollbar-track {
				border-radius: 16px;
		}
		&::-webkit-scrollbar-track {
				background-color: var(--color-background-100);
		}
		&::-webkit-scrollbar-track {
				padding: 0.25rem;
		}
		&::-webkit-scrollbar-thumb {
				border-radius: 16px;
		}
		&::-webkit-scrollbar-thumb {
				background-color: var(--color-neutral-900);
		}
		&::-webkit-scrollbar-thumb {
				--tw-shadow-color: var(--shadow-neutral);
				--tw-shadow: var(--tw-shadow-colored);
		}
		&::-webkit-scrollbar-thumb:hover {
				background-color: var(--color-neutral-300);
		}
		z-index: 42 !important;
}
	.options-list-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-100);
		border-radius: 12px;
		background-image: unset;
}
	.options-list-alternative2 {
		border-radius: 8px;
		border-color: var(--color-neutral-300);
		--tw-backdrop-blur: blur(100px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		background: var(--color-background-500);
}
	.options-list-alternative2-glass {
		background: color-mix(in srgb, var(--color-background-900) 80%, transparent);
		backdrop-filter: blur(30px);
		will-change: backdrop-filter;
	}
	.options-list.options-list-blur {
		background: var(--color-highlight-3);
		backdrop-filter: blur(8px);
		will-change: backdrop-filter;
		background-image: unset;
	}
	.options-list-alternative4 {
		border-width: 0px;
}
	.absolute-position {
		position: absolute;
		z-index: 10;
		max-height: 200px;
		width: 100%;
		overflow-y: auto;
}
	/* Portaled to <body>: fixed to the input's rect (set inline), immune to ancestor overflow/scroll
	   clipping — unlike toggling an ancestor's overflow, this never changes that ancestor's layout. */
	.options-list.fixed-position {
		position: fixed;
		max-height: 200px;
		overflow-y: auto;
		width: var(--dropdown-anchor-width);
		z-index: 2147483000 !important;
}
	.flow-content {
		margin-top: 0.25rem;
		width: 100%;
		overflow-y: auto;
}
	.option-item {
		display: flex;
		cursor: pointer;
		align-items: center;
		text-wrap: nowrap;
		border-bottom-width: 1px;
		border-color: var(--color-neutral-900);
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		color: var(--color-text-300);
}
	.option-item-alternative:hover,
	.option-item-alternative.selected {
		background-image: unset !important;
	}
	.option-item:hover,
	.option-item.active {
		background-color: var(--color-background-100);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.option-item.selected {
		background-color: var(--color-background-100);
}
	.options-list > .option-item:last-child {
		border-bottom-width: 0px;
}

	.options-list > a:last-child .option-item {
		border-bottom-width: 0px;
}
	.option-item-alternative2 {
		border-bottom-width: 1px;
		border-color: var(--color-neutral-500);
}
	.option-icon {
		margin-right: 0.5rem;
		height: 20px;
}
	.option-label {
		margin-left: 0.25rem;
		font-size: 12px;
}
	.input-container.extra-large {
		height: 70px;
		font-size: 14px;
}
	.input-container.large {
		height: 48px;
		font-size: 14px;
}
	.input-container.medium {
		height: 40px;
		font-size: 14px;
}
	.input-container.small {
		height: 36px;
		font-size: 12px;
}
	.options-list.extra-large {
		top: 74px;
}
	.options-list.large {
		top: 52px;
}
	.options-list.medium {
		top: 44px;
}
	.options-list.small {
		top: 40px;
}
	.menu-position-up.extra-large {
		top: -126px;
}
	.menu-position-up.large {
		top: -148px;
}
	.menu-position-up.medium {
		top: -156px;
}
	.menu-position-up.small {
		top: -160px;
}

	.dropdown-background-image-container {
		position: absolute;
		inset: 0px;
		height: 100%;
		width: 100%;
		overflow: hidden;
		border-radius: inherit;
}

	.dropdown-background-image {
		position: absolute;
		top: 0px;
		max-width: 100px;
		opacity: 0.2;
		left: 80%;
		transform: translateX(-50%) translateY(-50%);
		width: 100%;
		height: auto;
		-o-object-fit: cover;
		   object-fit: cover;
}
</style>
